import AppSettings from '../AppSettings';
// Librairies
import Axios from 'axios';
import Cookies from 'universal-cookie';
// Utils
import { showToast } from '../utils/ToastsUtil';
import { isNetworkError, readListFromSnapshot, readListSnapshotFirst, saveListToSnapshot, withPendingListItems } from '../utils/offline/OfflineRead';
import { canUseFieldWrite, submitFieldWrite } from '../utils/offline/FieldWrite';
import { getOfflineServices } from '../utils/offline/OfflineServices';
import { isLocalId, newLocalNumericId } from '../utils/offline/IdMap';
import { cleanupRemovedOps } from '../utils/offline/LocalCleanup';
import i18n from '../locales/i18n';
import { isOnline } from '../utils/offline/Connectivity';

const BACKEND_ENDPOINT = AppSettings.getBackendUrl();

export default class EventsService {
    static getEvents() {
        return Axios.get(BACKEND_ENDPOINT + 'events/', {
            headers: { 'Authorization': new Cookies().get('token') ? `Bearer ${new Cookies().get('token')}` : '' }
        }).then(response => {
            return response.data;
        }, () => {
            if (isOnline()) showToast('events_loading_failed');
            return;
        });
    }

    // fallback : copie relue sans réseau (faux pour un rafraîchissement)
    static async getProjectEvents(projectId, { fallback = true } = {}) {
        const fromSnapshot = await readListSnapshotFirst(projectId, 'projectEvents');
        if (fromSnapshot !== undefined) return fromSnapshot;
        return Axios.get(BACKEND_ENDPOINT + 'events/projectEvents/' + projectId, {
            headers: { 'Authorization': `Bearer ${new Cookies().get('token')}` }
        }).then(async response => {
            const data = await withPendingListItems(projectId, 'projectEvents', response.data);
            saveListToSnapshot(projectId, 'projectEvents', data);
            return data;
        }, async (error) => {
            if (fallback && isNetworkError(error)) {
                const cached = await readListFromSnapshot(projectId, 'projectEvents');
                if (cached !== undefined) return cached;
            }
            if (isOnline()) showToast('events_loading_failed');
            return;
        });
    }

    static addProjectEvent(projectEvent, projectId) {
        projectEvent.eventId = projectEvent.event.id;
        if (canUseFieldWrite()) return EventsService.addProjectEventThroughOutbox(projectEvent, projectId);
        return Axios.post(BACKEND_ENDPOINT + 'events/', projectEvent, {
            headers: { 'Authorization': `Bearer ${new Cookies().get('token')}` }
        }).then(response => {
            showToast('event_added');
            return response.data;
        }, () => {
            if (isOnline()) showToast('connection_failed');
            return;
        });
    }

    static updateProjectEvent(projectEvent, projectId, { peeToAdd = [], peeToRemove = [], successToast = 'event_updated', errorToast = 'event_update_failed' } = {}) {
        if (canUseFieldWrite()) return EventsService.updateProjectEventThroughOutbox(projectEvent, projectId, { peeToAdd, peeToRemove, successToast, errorToast });
        return Axios.put(BACKEND_ENDPOINT + 'events/', { projectEvent, peeToAdd, peeToRemove }, {
            headers: { 'Authorization': `Bearer ${new Cookies().get('token')}` }
        }).then(response => {
            showToast(successToast);
            return response.data;
        }, () => {
            if (isOnline()) showToast(errorToast);
            return;
        });
    }

    static removeProjectEvent(id, projectId) {
        if (canUseFieldWrite()) return EventsService.removeProjectEventThroughOutbox(id, projectId);
        return Axios.delete(BACKEND_ENDPOINT + 'events/' + id, {
            headers: { 'Authorization': `Bearer ${new Cookies().get('token')}` }
        }).then(response => {
            showToast('event_deleted');
            return response.data;
        }, () => {
            if (isOnline()) showToast('event_deletion_failed');
            return;
        });
    }

    static updateProjectEventElements(projectId, peId, projectEventElements) {
        if (canUseFieldWrite()) return EventsService.updateProjectEventElementsThroughOutbox(projectId, peId, projectEventElements);
        return Axios.put(BACKEND_ENDPOINT + 'events/updateProjectEventElements/?projectId=' + projectId + '&projectEventId=' + peId, projectEventElements, {
            headers: { 'Authorization': `Bearer ${new Cookies().get('token')}` }
        }).then(response => {
            showToast('event_updated');
            return response.data;
        }, () => {
            if (isOnline()) showToast('connection_failed');
            return;
        });
    }

    // Par la file : évènement à id local négatif, ses liens portent cet id ; l'objet « event » (catalogue) n'entre pas dans la file
    static addProjectEventThroughOutbox(projectEvent, projectId) {
        const localId = newLocalNumericId();
        const localEvent = { ...projectEvent, id: localId, projectId, projectEventElements: (projectEvent.projectEventElements || []).map(pee => ({ ...pee, projectEventId: localId })) };
        const { event, ...storedEvent } = localEvent;
        return submitFieldWrite({
            op: {
                type: 'event.create', projectId, target: { entity: 'event', id: localId },
                refs: localEvent.projectEventElements.map(pee => ({ entity: 'element', id: pee.elementId })),
                payload: { projectEvent: storedEvent }
            },
            buildLocalData: () => localEvent
        }).then(response => {
            showToast(response.isLocal ? 'saved_on_device' : 'event_added');
            return response.data;
        }, () => {
            showToast('connection_failed');
            return;
        });
    }

    // Seuls la date et le commentaire changés depuis la copie partent (les seuls champs que l'API recopie)
    static async updateProjectEventThroughOutbox(projectEvent, projectId, { peeToAdd, peeToRemove, successToast, errorToast }) {
        const previous = ((await getOfflineServices().snapshot.getList(projectId, 'projectEvents')) || []).find(pe => pe.id === projectEvent.id);
        const changes = Object.fromEntries(['date', 'comment']
            .filter(field => !previous || JSON.stringify(previous[field] ?? null) !== JSON.stringify(projectEvent[field] ?? null))
            .map(field => [field, projectEvent[field]]));
        const updatedEvent = {
            ...projectEvent,
            projectEventElements: [
                ...(projectEvent.projectEventElements || []).filter(pee => !peeToRemove.some(removed => removed.elementId === pee.elementId)),
                ...peeToAdd.filter(pee => !(projectEvent.projectEventElements || []).some(existing => existing.elementId === pee.elementId)).map(pee => ({ ...pee, projectEventId: projectEvent.id }))
            ]
        };
        return submitFieldWrite({
            op: {
                type: 'event.update', projectId, target: { entity: 'event', id: projectEvent.id },
                refs: [...peeToAdd.map(pee => ({ entity: 'element', id: pee.elementId })), ...(isLocalId('event', projectEvent.id) ? [{ entity: 'event', id: projectEvent.id }] : [])],
                payload: { changes, peeToAdd, peeToRemove }
            },
            buildLocalData: () => updatedEvent
        }).then(response => {
            showToast(response.isLocal ? 'saved_on_device' : successToast);
            return response.data;
        }, () => {
            showToast(errorToast);
            return;
        });
    }

    // Un évènement créé sur l'appareil et jamais envoyé est simplement retiré, avec ses saisies en attente
    static async removeProjectEventThroughOutbox(id, projectId) {
        const services = getOfflineServices();
        if (isLocalId('event', id)) {
            const { cancelledKeys, removedOps } = await services.outbox.cancelLocalCreations([`event:${id}`]);
            await cleanupRemovedOps(services, removedOps);
            if (cancelledKeys.length) {
                showToast('event_deleted');
                return null;
            }
        }
        return submitFieldWrite({ op: { type: 'event.delete', projectId, target: { entity: 'event', id } }, buildLocalData: () => null })
            .then(response => {
                showToast(response.isLocal ? 'saved_on_device' : 'event_deleted');
                return response.data;
            }, () => {
                showToast('event_deletion_failed');
                return;
            });
    }

    // Statut et commentaire écrits dans la copie : les appelants modifient le store sur place, que ProjectMap ne réenregistre pas
    static updateProjectEventElementsThroughOutbox(projectId, peId, projectEventElements) {
        const withValues = (list, values) => list?.map(pe => (pe.id !== peId ? pe : {
            ...pe, projectEventElements: (pe.projectEventElements || []).map(pee => (values.has(pee.elementId) ? { ...pee, ...values.get(pee.elementId) } : pee))
        }));
        let previousValues = null;
        return submitFieldWrite({
            op: {
                type: 'event.updateElements', projectId, target: { entity: 'eventElements', id: String(peId) },
                touches: [`event:${peId}`], refs: isLocalId('event', peId) ? [{ entity: 'event', id: peId }] : [],
                payload: { projectEventId: peId, projectEventElements }
            },
            applyLocal: services => services.snapshot.updateList(projectId, 'projectEvents', list => {
                const projectEvent = list?.find(pe => pe.id === peId);
                previousValues = new Map((projectEvent?.projectEventElements || []).map(pee => [pee.elementId, { status: pee.status, comment: pee.comment }]));
                return withValues(list, new Map(projectEventElements.map(pee => [pee.elementId, { status: pee.status, comment: pee.comment }])));
            }),
            revertLocal: services => (previousValues ? services.snapshot.updateList(projectId, 'projectEvents', list => withValues(list, previousValues)) : null),
            buildLocalData: () => projectEventElements
        }).then(response => {
            showToast(response.isLocal ? 'saved_on_device' : 'event_updated');
            return response.data;
        }, () => {
            showToast('connection_failed');
            return;
        });
    }

    static exportEventsAsXLSX(baseProject, rows, groupedBy, columnsToRender) {
        return Axios.post(BACKEND_ENDPOINT + 'events/exportXLSX/', { baseProjectId: baseProject.id, rows, groupedBy, columnsToRender }, {
            responseType: 'blob',
            headers: {
                'Authorization': `Bearer ${new Cookies().get('token')}`,
                'Content-Type': 'application/json'
            }
        }).then(response => {
            const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/vnd.ms-excel' }));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `${baseProject.label} - ${i18n.t("Évènements")}.xlsx`);
            document.getElementById('modal-content').appendChild(link);
            link.click();
            document.getElementById('modal-content').removeChild(link);
            URL.revokeObjectURL(url);
        }, () => {
            if (isOnline()) showToast('elements_xlsx_export_failed');
        });
    }
}